import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import './NotificationManager.css';

const NotificationManager = () => {
  const { getAuthHeaders } = useAuth();

  const [form, setForm]         = useState({ title: '', image_url: '', message: '' });
  const [sending, setSending]   = useState(false);
  const [result, setResult]     = useState(null);   // null | { ok, message }
  const [history, setHistory]   = useState([]);
  const [imgUpload, setImgUpload] = useState({ uploading: false, error: null });
  const [clearing, setClearing]   = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await fetch('/api/v1/notifications/broadcast');
      const data = await response.json();
      if (data.success) {
        setHistory(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch notification history:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImgUpload({ uploading: true, error: null });

    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch('/api/v1/notifications/upload-image', {
        method: 'POST',
        headers: { ...getAuthHeaders() },
        body: formData
      });
      const data = await response.json();
      if (data.success) {
        setForm(prev => ({ ...prev, image_url: data.url }));
        setImgUpload({ uploading: false, error: null });
      } else {
        setImgUpload({ uploading: false, error: 'Upload failed' });
      }
    } catch {
      setImgUpload({ uploading: false, error: 'Upload failed' });
    }

    // Reset file input so the same file can be re-selected if needed
    e.target.value = '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);

    try {
      const response = await fetch('/api/v1/notifications/broadcast', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          title: form.title,
          description: form.message,
          image_url: form.image_url || undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult({ ok: true, message: data.message });
        setHistory(prev => [data.data, ...prev]);
        setForm({ title: '', image_url: '', message: '' });
      } else {
        setResult({ ok: false, message: data.message || 'Failed to send notification' });
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      setResult({ ok: false, message: 'Failed to send notification' });
    } finally {
      setSending(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all sent notifications? This cannot be undone.')) return;
    setClearing(true);
    try {
      const response = await fetch('/api/v1/notifications/broadcast', {
        method: 'DELETE',
        headers: { ...getAuthHeaders() }
      });
      const data = await response.json();
      if (data.success) {
        setHistory([]);
      }
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    } finally {
      setClearing(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kuala_Lumpur'
    });
  };

  return (
    <Layout>
      <div className="notification-manager">

        {/* ── Section 1: Compose ── */}
        <div className="nm-section">
          <h1 className="nm-heading">Push Notifications</h1>

          <form onSubmit={handleSubmit} className="nm-form">
            <div className="form-group">
              <label className="form-label">
                Title <span className="nm-required">*</span>
              </label>
              <input
                type="text"
                name="title"
                className="form-input"
                value={form.title}
                onChange={handleChange}
                maxLength={120}
                placeholder="Notification title"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Image URL (optional)</label>
              <input
                type="text"
                name="image_url"
                className="form-input"
                value={form.image_url}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="Preview"
                  className="nm-image-preview"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e)  => { e.target.style.display = 'block'; }}
                />
              )}
              <div className="nm-upload-section">
                <span className="nm-upload-divider">or upload image</span>
                <input
                  type="file"
                  accept="image/*"
                  className="nm-upload-input"
                  onChange={handleImageUpload}
                  disabled={imgUpload.uploading}
                />
                {imgUpload.uploading && (
                  <span className="nm-upload-status nm-upload-loading">Uploading...</span>
                )}
                {imgUpload.error && (
                  <span className="nm-upload-status nm-upload-error">{imgUpload.error}</span>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Message / Description (optional)</label>
              <textarea
                name="message"
                className="form-input"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="Notification body text"
              />
            </div>

            <div className="nm-form-footer">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sending || !form.title.trim()}
              >
                {sending ? 'Sending…' : 'Send to all users'}
              </button>

              {result && (
                <p className={result.ok ? 'nm-result-success' : 'nm-result-error'}>
                  {result.message}
                </p>
              )}
            </div>
          </form>
        </div>

        {/* ── Section 2: Sent history ── */}
        <div className="nm-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 className="nm-heading">Sent history</h2>
            {history.length > 0 && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleClearAll}
                disabled={clearing}
                style={{ fontSize: '0.85rem', padding: '6px 14px' }}
              >
                {clearing ? 'Clearing…' : 'Clear all'}
              </button>
            )}
          </div>

          {history.length === 0 ? (
            <p className="nm-empty">No notifications sent yet</p>
          ) : (
            <div className="nm-history-list">
              {history.map((notif, idx) => (
                <div key={notif.id ?? idx} className="nm-history-item">
                  <div className="nm-history-main">
                    <div className="nm-history-left">
                      <strong className="nm-history-title">{notif.title}</strong>
                      {notif.message && (
                        <p className="nm-history-message">{notif.message}</p>
                      )}
                      <span className="nm-history-meta">
                        {formatDate(notif.sent_at || notif.created_at)}
                        {' · '}
                        {notif.sent_count} recipient{notif.sent_count !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {notif.image_url && (
                      <img
                        src={notif.image_url}
                        alt=""
                        className="nm-history-thumb"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Layout>
  );
};

export default NotificationManager;
